import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ChevronLeft, ChevronRight, Download, Loader2, ZoomIn, ZoomOut, X } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const btn = "rounded-full p-2 text-stone-600 transition-colors duration-300 hover:bg-stone-100 hover:text-[#8a6b2e] disabled:opacity-30";

export default function PdfViewer({ doc, url, onClose }) {
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [width, setWidth] = useState(800);
  const box = useRef(null);

  useEffect(() => {
    const fit = () => box.current && setWidth(Math.min(box.current.clientWidth - 16, 900));
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  return (
    <div data-testid="pdf-viewer" className="fixed inset-0 z-50 flex flex-col bg-[#FAF8F5]">
      <div className="flex items-center justify-between gap-3 border-b border-stone-200 bg-white px-4 py-3">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C5A059]">{doc.category}</p>
          <h3 data-testid="pdf-viewer-title" className="truncate font-display text-lg font-semibold text-stone-900">{doc.title}</h3>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button data-testid="pdf-zoom-out" onClick={() => setZoom((z) => Math.max(0.6, z - 0.2))} className={btn} aria-label="Dézoomer"><ZoomOut className="h-4 w-4" /></button>
          <button data-testid="pdf-zoom-in" onClick={() => setZoom((z) => Math.min(2.4, z + 0.2))} className={btn} aria-label="Zoomer"><ZoomIn className="h-4 w-4" /></button>
          <a data-testid="pdf-download" href={url} download={`${doc.title}.pdf`} className="ml-1 inline-flex items-center gap-1.5 rounded-full bg-[#1C1917] px-4 py-2 text-xs font-semibold text-[#FAF8F5] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#1C1917]">
            <Download className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Télécharger</span>
          </a>
          <button data-testid="pdf-close" onClick={onClose} className={btn} aria-label="Fermer"><X className="h-5 w-5" /></button>
        </div>
      </div>

      <div ref={box} className="flex-1 overflow-auto px-2 py-6">
        <Document
          file={url}
          onLoadSuccess={({ numPages }) => setPages(numPages)}
          loading={<p className="flex items-center justify-center gap-2 py-20 text-sm text-stone-500"><Loader2 className="h-4 w-4 animate-spin" /> Chargement du document…</p>}
          error={<p data-testid="pdf-error" className="py-20 text-center text-sm text-[#C85A32]">Impossible d'afficher ce PDF. Utilisez le bouton Télécharger.</p>}
          className="flex justify-center"
        >
          <Page pageNumber={page} width={width * zoom} className="overflow-hidden rounded-lg shadow-lg" />
        </Document>
      </div>

      {pages > 0 && (
        <div className="flex items-center justify-center gap-4 border-t border-stone-200 bg-white py-3" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <button data-testid="pdf-prev" onClick={() => setPage((p) => p - 1)} disabled={page <= 1} className={btn} aria-label="Page précédente"><ChevronLeft className="h-5 w-5" /></button>
          <span data-testid="pdf-page-indicator" className="text-sm font-medium text-stone-600">Page {page} / {pages}</span>
          <button data-testid="pdf-next" onClick={() => setPage((p) => p + 1)} disabled={page >= pages} className={btn} aria-label="Page suivante"><ChevronRight className="h-5 w-5" /></button>
        </div>
      )}
    </div>
  );
}
